// src/api/auth.ts
import api, {
  getAuth,
  setAuth,
  clearAuth,
  type AuthUser, // 👈 reuse the shared AuthUser (includes firstName/lastName)
} from "@/api/client";
import { PATHS } from "@/api/constants";

export interface MeResponse {
  ok: boolean;
  data?: AuthUser;
  message?: string;
}

/**
 * verifySession (cookie-mode)
 * - Calls /auth/me (cookie sent automatically)
 * - On success: caches latest user
 * - On 401/403: clears cache so guards can redirect
 */
export async function verifySession(): Promise<AuthUser | null> {
  try {
    const res = await api.get<MeResponse>(PATHS.auth.me);
    const { ok, data, message } = res.data ?? {};

    if (!ok || !data) {
      // Treat non-ok as unauthorized/invalid session
      const err: any = new Error(message || "Invalid /me response");
      err.statusCode = 401;
      throw err;
    }

    // persist the latest user snapshot
    const current = getAuth();
    setAuth({ ...current, user: data });
    return data;
  } catch (err: any) {
    const status = err?.response?.status ?? err?.statusCode;
    if (status === 401 || status === 403) {
      clearAuth(); // ensure local logout
    }
    throw err;
  }
}

/** Simple getter for cached user (UI-only) */
export function getCachedUser(): AuthUser | null {
  return (getAuth().user as AuthUser) ?? null;
}

/* ------------------------------------------------------------------
 * Change Password
 * ------------------------------------------------------------------ */

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export interface ChangePasswordResponse {
  ok: boolean;
  message?: string;
}

export async function changePassword(
  payload: ChangePasswordPayload
): Promise<ChangePasswordResponse> {
  const res = await api.post<ChangePasswordResponse>(
    PATHS.auth.changePassword, // make sure this is defined in PATHS.auth
    payload
  );

  const { ok, message } = res.data ?? {};
  if (!ok) {
    throw new Error(message || "Failed to change password.");
  }

  return res.data;
}
